import { useEffect, useRef, useState } from 'react';
import { animate } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { PieChart } from 'lucide-react';

interface BreakdownItem {
    label: string;
    value: number;
    color: string;
}

interface ComplianceBreakdownProps {
    data: BreakdownItem[];
    title?: string;
    centerLabel?: string;
}

/**
 * ComplianceBreakdown - Animated donut chart showing compliance distribution
 * Features animated segments and interactive legend
 */
export default function ComplianceBreakdown({
    data,
    title = 'Compliance Breakdown',
    centerLabel = 'Total',
}: ComplianceBreakdownProps) {
    const { ref, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const segmentsRef = useRef<(SVGCircleElement | null)[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const total = data.reduce((sum, item) => sum + item.value, 0);
    const size = 180;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    // Calculate segment offsets
    const segments = data.map((item, index) => {
        const previousValues = data.slice(0, index).reduce((sum, i) => sum + i.value, 0);
        const offset = (previousValues / total) * circumference;
        const length = (item.value / total) * circumference;
        return { ...item, offset, length };
    });

    useEffect(() => {
        if (!isInView || reducedMotion) return;

        segmentsRef.current.forEach((segment, index) => {
            if (!segment) return;

            const targetLength = segments[index].length;

            animate(segment, {
                strokeDasharray: [`0 ${circumference}`, `${targetLength} ${circumference}`],
                opacity: [0, 1],
                duration: 1200,
                delay: index * 150,
                ease: 'outExpo',
            });
        });
    }, [isInView, segments, circumference, reducedMotion]);

    return (
        <div
            ref={ref}
            className={`card-terminal p-6 ${isInView ? 'animate-fadeIn' : 'opacity-0'}`}
        >
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                    <h3 className="font-display font-semibold text-white">{title}</h3>
                    <p className="text-sm text-gray-500">By category</p>
                </div>
            </div>

            <div className="flex items-center gap-8">
                {/* Donut Chart */}
                <div className="relative" style={{ width: size, height: size }}>
                    <svg
                        width={size}
                        height={size}
                        className="transform -rotate-90"
                    >
                        {/* Background circle */}
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke="#333333"
                            strokeWidth={strokeWidth}
                        />

                        {/* Segments */}
                        {segments.map((segment, index) => (
                            <circle
                                key={index}
                                ref={el => segmentsRef.current[index] = el}
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="none"
                                stroke={segment.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={reducedMotion ? `${segment.length} ${circumference}` : `0 ${circumference}`}
                                strokeDashoffset={-segment.offset}
                                strokeLinecap="round"
                                className={`transition-all duration-200 cursor-pointer ${hoveredIndex === index ? 'filter drop-shadow-lg' : ''
                                    }`}
                                style={{
                                    transform: hoveredIndex === index ? 'scale(1.05)' : 'scale(1)',
                                    transformOrigin: 'center',
                                }}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            />
                        ))}
                    </svg>

                    {/* Center label */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-display font-bold text-white">
                            {total}
                        </span>
                        <span className="text-xs text-gray-500">{centerLabel}</span>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex-1 space-y-3">
                    {data.map((item, index) => {
                        const percentage = ((item.value / total) * 100).toFixed(1);
                        const isHovered = hoveredIndex === index;

                        return (
                            <div
                                key={index}
                                className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-200 cursor-pointer ${isHovered ? 'bg-white/5' : ''
                                    }`}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: item.color }}
                                />
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-300">
                                            {item.label}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {item.value} ({percentage}%)
                                        </span>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="mt-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-500"
                                            style={{
                                                width: `${percentage}%`,
                                                backgroundColor: item.color,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
